import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Fade, Modal as MUIModal, Skeleton, Stack, Typography,
} from '@mui/material';
import _ from 'lodash';

import {
  BaseColorKeys, NotifierColorKeys, SubjectColorKeys,
} from '../../theme';
import CollapsableNotification from '../CollapsableNotification';
import EditField, { FieldType } from '../EditField';

import {
  ActionButton,
  ActionContainer,
  CloseIconButton,
  Container,
  DetailDivider,
  FieldEntry,
  FieldName,
  FieldRow,
  FieldsContainer,
  TitleContainer,
} from './index.styles';

const SkeletonContent = () => (
  <>
    <Stack spacing={1}>
      <Skeleton variant="rectangular" height={24} width={180} />
      <Skeleton variant="rectangular" height={12} width={360} />
    </Stack>
    <DetailDivider />
    <Stack spacing={1}>
      <Skeleton variant="rectangular" height={12} width={360} />
      <Skeleton variant="rectangular" height={12} width={360} />
      <Skeleton variant="rectangular" height={12} width={320} />
    </Stack>
  </>
);

export type TypeOption = {
  label: string,
  value: any
}

export type Field = {
  editable: boolean,
  label?: string,
  hide?: boolean,
  type: FieldType,
  typeOptions?: TypeOption[],
}

export type Fields = {
  [key: string]: Field
}

export type Values = {
  [key: string]: any,
}

export type Button = {
  fn: (values?: Values, newValues?: Values) => void,
  label?: string,
  loading?: boolean,
  color?: BaseColorKeys | NotifierColorKeys,
  disabled?: boolean | ((newValues?: Values) => boolean),
  variant?: 'contained' | 'outlined' | 'text',
  clearValuesOnClick?: boolean,
}

interface DetailsModalProps {
  open: boolean,
  handleClose: (event?: object, reason?: string) => void
  hideCloseIcon?: boolean,
  title?: string,
  subtitle?: string,
  fields?: Fields,
  values?: Values,
  loading?: boolean,
  onFieldChange?: (key: string, newValue: string | number | boolean | (string | number)[]) => void,
  buttons?: Button[],
  error?: any,
  titleColor?: BaseColorKeys | SubjectColorKeys | NotifierColorKeys,
}

const DetailsModal = ({
  open = false,
  handleClose = () => {},
  hideCloseIcon = false,
  title,
  subtitle,
  fields = {},
  values = {},
  loading = false,
  onFieldChange = () => {},
  buttons = [],
  error,
  titleColor = 'primary',
}: DetailsModalProps) => {
  const [newValues, setNewValues] = useState<Values>();
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const [errorOpen, setErrorOpen] = useState(!!error);

  useEffect(() => {
    if (open === false) {
      setNewValues(undefined);
      setErrorOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (error) setErrorOpen(true);
    else setErrorOpen(false);
  }, [error]);

  return (
    <MUIModal
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Container>
          {!hideCloseIcon && (
            <CloseIconButton onClick={handleClose}>
              <CloseIcon />
            </CloseIconButton>
          )}
          {loading ? <SkeletonContent /> : (
            <>
              <TitleContainer color={titleColor}>
                <Typography variant="h4">{title}</Typography>
              </TitleContainer>
              <Typography variant="caption">{subtitle}</Typography>
              <DetailDivider />
              <CollapsableNotification
                open={errorOpen}
                close={() => setErrorOpen(false)}
                message={error?.data?.message}
              />
              <FieldsContainer>
                {Object.keys(fields).map((key, i) => (!!fields[key]?.hide === true ? null : (
                  <FieldRow key={key}>
                    <FieldName variant="subtitle2">
                      {fields[key]?.label || _.startCase(key)}
                    </FieldName>
                    <FieldEntry>
                      <EditField
                        editMode={focusIndex === i}
                        value={values[key]}
                        type={fields[key].type}
                        typeOptions={fields[key]?.typeOptions}
                        disabled={buttons.some((b) => b?.loading) || !fields[key]?.editable}
                        onSubmit={(value) => {
                          setNewValues({ ...newValues, [key]: value });
                          onFieldChange(key, value);
                        }}
                        onTab={() => setFocusIndex(i + 1)}
                        // Reset forceful focus on manual focus
                        onClick={() => setFocusIndex(null)}
                      />
                    </FieldEntry>
                  </FieldRow>
                )))}
              </FieldsContainer>
              <ActionContainer>
                {buttons.map((button) => (
                  <ActionButton
                    // Use a composite of the button label and color as the key
                    key={`${button?.label}${button?.color}`}
                    size="medium"
                    color={button?.color || 'darkPrimary'}
                    variant={button?.variant}
                    onClick={async () => {
                      await button?.fn({ ...values, ...newValues }, newValues);

                      // Reset newValues since action has been taken
                      if (button?.clearValuesOnClick) {
                        setNewValues(undefined);
                      }
                    }}
                    loading={button?.loading}
                    disabled={typeof button?.disabled === 'function' ? button?.disabled(newValues) : button?.disabled}
                  >{button?.label || 'Action'}
                  </ActionButton>
                ))}
              </ActionContainer>
            </>
          )}
        </Container>
      </Fade>
    </MUIModal>
  );
};

export default DetailsModal;
